<template>
  <LazyHydrate v-bind="hydrationStrategy">
    <!-- We keep the same HTML structure for previous styles, so existing custom CSS styling still works for them -->
    <HeroBannerOld
      v-if="blok.style !== 'cards'"
      v-bind="$attrs"
      :blok="blok"
      :context="context"
    />
    <div
      v-else
      v-bind="$attrs"
      :class="blok.style === 'cards' ? 'card-style' : ''"
    >
      <ResponsiveWrapper :component="blok.component" :devices="blok.devices">
        <SectionWrapper
          :blok="blok"
          padding="none"
          class="lg:bg-transparent"
          :class="backgroundColorClass(blok.bgVariant)"
        >
          <div
            class="relative flex flex-col lg:flex-row"
            :class="{
              'flex-col-reverse': !blok.mobileImageFirst,
              'lg:flex-row-reverse': contentPosition === 'right',
              'lg:justify-center':
                contentPosition === 'center' || contentPosition === 'bottom',
            }"
          >
            <HeroBannerImage
              v-if="hasImageOrVideo"
              :class="backgroundColorClass(blok.bgVariant)"
              :blok="blockWithStyle"
              :img-url="imgUrl"
              :img-alt="imgAlt"
              :video="blok.backgroundVideo"
              :video-mobile="blok.backgroundVideoMobile"
            />
            <div
              v-else
              class="absolute hidden h-full w-full lg:block"
              :class="backgroundColorClass(blok.bgVariant)"
            />

            <div
              class="text-container relative flex w-full flex-col text-center lg:text-left"
              :class="[
                {
                  'max-w-7xl justify-center':
                    contentPosition === 'center' ||
                    contentPosition === 'bottom',
                  'lg:w-1/2 lg:py-36': imgUrl && contentPosition !== 'bottom',
                  'lg:w-1/2 lg:py-24': !imgUrl && contentPosition !== 'bottom',
                  'z-1 lg:-mb-[6rem] lg:w-auto lg:pt-96 lg:pb-0':
                    contentPosition === 'bottom',
                  'lg:mt-12': blok.style === 'cards',
                  'lg:bg-transparent':
                    blockWithStyle.backgroundPosition === 'full-width',
                },
                backgroundColorClass(blok.bgVariant),
              ]"
            >
              <div
                class="relative w-full"
                :class="[
                  contentPosition === 'bottom'
                    ? 'mx-auto drop-shadow-xl xl:w-[64rem]'
                    : 'xl:w-[40rem]',
                  {
                    'ml-auto': contentPosition === 'left',
                    'mr-auto': contentPosition === 'right',
                  },
                ]"
              >
                <div
                  class="absolute hidden h-full w-full lg:block"
                  :class="
                    blockWithStyle.displayBackground &&
                    blockWithStyle.backgroundPosition === 'full-width'
                      ? `${blok.bgVariant ? backgroundColorClass(blok.bgVariant) : ''} opacity-${bgOpacity}`
                      : ''
                  "
                />
                <div
                  class="content-container px-4 py-8 sm:p-8 lg:relative"
                  :class="{
                    'gap-16 lg:flex lg:p-0': contentPosition === 'bottom',
                    'lg:p-16': contentPosition !== 'bottom',
                  }"
                >
                  <div
                    class="first-section relative"
                    :class="{
                      'grid lg:px-12 lg:pt-6 lg:pb-12':
                        contentPosition === 'bottom',
                    }"
                  >
                    <ImgAdapter
                      v-if="
                        (blok.logoImage && blok.logoImage?.filename) ||
                        blok.logoImageUrl
                      "
                      :img-sources="[blok.logoImage, blok.logoImageUrl]"
                      :height="80"
                      :alt="logoAlt"
                      class="mx-auto h-[4rem]"
                      :class="contentPosition !== 'center' ? 'lg:mx-0' : ''"
                    />
                    <h1
                      class="title"
                      :class="[
                        textColorClass('', blok.bgVariant, 'text-darker'),
                        {
                          'lg:text-center': contentPosition === 'center',
                          'font-medium lg:text-5xl lg:leading-12':
                            blok.style === 'cards',
                        },
                      ]"
                    >
                      <span v-if="blok.beforeTitleHero">{{
                        blok.beforeTitleHero + ' '
                      }}</span>
                      <span
                        v-if="blok.titleHero"
                        :class="
                          textColorClass(blok.titleVariant, blok.bgVariant)
                        "
                      >
                        {{ blok.titleHero }}
                      </span>
                      <span v-if="blok.afterTitleHero">{{
                        ' ' + blok.afterTitleHero
                      }}</span>
                    </h1>
                    <h2
                      v-if="blok.subtitle"
                      class="subtitle text-lg sm:text-xl"
                      :class="[
                        textColorClass(
                          blok.subtitleVariant,
                          blok.bgVariant,
                          'text-darker',
                        ),
                        contentPosition === 'center'
                          ? 'lg:text-center'
                          : 'md:max-w-3xl',
                        {
                          'row-start-1 mb-4': contentPosition === 'bottom',
                          'mt-4':
                            contentPosition !== 'bottom' &&
                            blok.style !== 'cards',
                          'mt-6': blok.style === 'cards',
                        },
                      ]"
                    >
                      {{ blok.subtitle }}
                    </h2>
                    <RichText
                      :blok="blok"
                      :class="[
                        contentPosition === 'bottom' ? 'mt-6' : 'mt-8',
                        { 'lg:text-xl': blok.style === 'cards' },
                      ]"
                    />
                    <WidthWrapper
                      width="full"
                      class="ml-0"
                      :class="{ 'w-full': contentPosition === 'bottom' }"
                    >
                      <div
                        v-if="
                          blok.callToActionButtons &&
                          blok.callToActionButtons.length
                        "
                        class="mt-8 flex justify-start md:flex-row"
                        :class="{
                          'flex-row space-x-4': !blok.stackButtonsAsColumn,
                          'flex-col space-y-4 md:space-y-0 md:space-x-4':
                            blok.stackButtonsAsColumn,
                          'lg:justify-center': contentPosition === 'center',
                        }"
                      >
                        <component
                          :is="btn.component"
                          v-for="btn in blok.callToActionButtons"
                          :key="btn._uid"
                          :item="btn"
                          :blok="blok"
                          :context="context"
                          class="w-auto text-center"
                        />
                      </div>
                    </WidthWrapper>
                  </div>
                  <div
                    class="second-section relative"
                    :class="{
                      'shrink-0': contentPosition === 'bottom',
                    }"
                  >
                    <RichText
                      v-if="blok.bottomTextContent"
                      :blok="blok"
                      :rich-text-content="blok.bottomTextContent"
                      class="mt-4"
                      :class="{
                        'mt-8 px-6 py-8 lg:mt-0 lg:w-72 lg:pt-12':
                          contentPosition === 'bottom',
                      }"
                    />
                    <LogoListBase
                      v-if="hasLogos"
                      :blok="blok"
                      class="logo-list mt-4 justify-center space-x-5 md:mt-12"
                      :class="
                        contentPosition !== 'center' ? 'sm:justify-start' : ''
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <nav
            v-if="blok.navigation?.length"
            class="relative -bottom-24 z-1 -mt-24 flex w-full justify-center lg:-top-12 lg:bottom-0"
            aria-label="koptekst banner navigatie"
          >
            <ul
              class="mx-4 flex max-w-5xl flex-col divide-y overflow-hidden rounded-md drop-shadow-xl sm:mx-8 md:flex-row md:flex-wrap md:justify-center md:divide-x lg:mx-auto lg:flex-nowrap lg:divide-y-0"
            >
              <li v-for="item in blok.navigation" :key="item._uid" class="flex">
                <NavigationCard
                  class="w-full md:w-64"
                  :item="item"
                  :blok="{ ...blok, bgVariant: 'white' }"
                />
              </li>
            </ul>
          </nav>
        </SectionWrapper>
      </ResponsiveWrapper>
    </div>
  </LazyHydrate>
</template>

<script setup>
import { computed } from 'vue'
import HeroBannerOld from './HeroBannerOld.vue'
import ResponsiveWrapper from '../components/ResponsiveWrapper.vue'
import WidthWrapper from '../components/WidthWrapper.vue'
import SectionWrapper from '../components/SectionWrapper.vue'
import NavigationCard from '../components/NavigationCard.vue'
import { useColorPalette } from '../composables/colorPalette'
import ImgAdapter from '../components/ImgAdapter.vue'
import RichText from '../components/RichText.vue'
import HeroBannerImage from '../components/HeroBannerImage.vue'
import LogoListBase from '../components/LogoListBase.vue'
import CallToActionButton from '../components/CallToActionButton.vue'
import CallToActionLink from '../components/CallToActionLink.vue'
import WhatsappButton from '../components/WhatsappButton.vue'
import FacebookButton from '../components/FacebookButton.vue'
import TwitterButton from '../components/TwitterButton.vue'
import LinkedinButton from '../components/LinkedinButton.vue'
import TiktokButton from '../components/TiktokButton.vue'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

// All of these components can be added dynamically to the hero banner
defineOptions({
  components: {
    CallToActionButton,
    CallToActionLink,
    WhatsappButton,
    FacebookButton,
    TwitterButton,
    LinkedinButton,
    TiktokButton,
  },
})

const { backgroundColorClass, textColorClass } = useColorPalette()

const hydrationStrategy = computed(() => {
  if (props.blok.style !== 'cards') {
    // Uses the old hero banner block with its own hydration strategy, so it should be hydrated as soon as possible
    return { whenIdle: true }
  } else {
    if (
      props.blok.backgroundVideo?.filename ||
      props.blok.backgroundVideoMobile?.filename
    ) {
      return { whenIdle: true }
    } else if (
      props.blok.navigation?.find((item) => item.displayVacanciesCounter)
    ) {
      return { whenIdle: true }
    } else {
      return {
        onInteraction: ['focus'],
      }
    }
  }
})
const blockWithStyle = computed(() => {
  const attrs = {}

  if (props.blok.style) {
    switch (props.blok.style) {
      case 'text-image':
        attrs.contentPosition = 'left'
        attrs.backgroundPosition = 'right'
        break
      case 'image-text':
        attrs.contentPosition = 'right'
        attrs.backgroundPosition = 'left'
        break
      case 'full_image-centered_text':
        attrs.contentPosition = 'center'
        attrs.backgroundPosition = 'full-width'
        attrs.displayBackground = true
        attrs.backgroundOpacity = 100
        break
      case 'full_image-bottom_text':
        attrs.contentPosition = 'bottom'
        attrs.backgroundPosition = 'full-width'
        attrs.displayBackground = true
        attrs.backgroundOpacity = 100
        break
      case 'cards':
        attrs.contentPosition = 'left'
        attrs.backgroundPosition = 'right'
        break
    }
  }

  const setProps = Object.fromEntries(
    Object.entries(props.blok).filter(([key, value]) => !!props.blok[key]),
  )

  // Specific settings set in Storyblok override style's defaults
  return { ...attrs, ...setProps }
})
const contentPosition = computed(() => {
  return blockWithStyle.value.contentPosition || 'left'
})
const bgOpacity = computed(() => {
  return parseInt(blockWithStyle.value.backgroundOpacity) || 100
  // opacity-10 opacity-20 opacity-30 opacity-40 opacity-50 opacity-60 opacity-70 opacity-80 opacity-90 opacity-100
})
const hasLogos = computed(() => {
  return props.blok.logos?.length
})
const imgUrl = computed(() => {
  return (
    (props.blok.backgroundImage && props.blok.backgroundImage?.filename) ||
    props.blok.backgroundImageUrl ||
    ''
  )
})
const imgAlt = computed(() => {
  return props.blok.backgroundImage?.filename
    ? props.blok.backgroundImage?.alt
    : ''
})
const logoAlt = computed(() => {
  return props.blok.logoImage?.filename ? props.blok.logoImage?.alt : ''
})
const hasImageOrVideo = computed(() => {
  return (
    !!imgUrl.value ||
    !!props.blok.backgroundVideo?.filename ||
    !!props.blok.backgroundVideoMobile?.filename
  )
})
</script>

<style scoped>
@reference "../css/theme.css";

.card-style h2.subtitle {
  @apply lg:text-2xl;
}

/* We need to add this Tailwind class here so it can override the custom bg color classes */
@media (min-width: 992px) {
  .lg\:bg-transparent {
    background-color: transparent;
  }
}
</style>
